// Global constants for the application

/** IANA timezone for business dates, exports, and reports (Phnom Penh). */
export const APP_TIMEZONE = "Asia/Phnom_Penh" as const;

// Pagination constants
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  FIRST_PAGE: 1,
  FIRST_ROW_INDEX: 1,
  MAX_PAGE_SIZE: 5,
  ROTATE: true,
  BOUNDARY_LINKS: true,
} as const;

// Timer constants
export const TIMERS = {
  SUCCESS: 1500,
  ERROR: 2000,
  SEARCH_DEBOUNCE_TIME: 500,
  QR_DOWNLOAD_DELAY: 1000  // Thêm constant cho QR download delay
} as const;

// Status constants
export const STATUS = {
  ENABLED: "enabled",
  DISABLED: "disabled",
} as const;

// Spinner constants
export const SPINNER = {
  STATUS: "success",
  MESSAGE: "Loading...",
  SIZE: "large",
  TINY_SIZE: "tiny",
} as const;

// URL constants
export const URLS = {
  NO_BANNER_URL: "./assets/images/image-placeholder.png",
  NO_PRODUCTS_IMAGE: "./assets/images/no-products.png",
  IMAGE_PLACEHOLDER: "./assets/images/image-placeholder.png",
  EDIT_ICON: "./assets/images/pencil-square.png",
  DELETE_ICON: "./assets/images/trash.png",
  ADD_ICON: "./assets/images/plus-circle.png",
  CLOCK_ICON: "./assets/images/clock.png",
  ICON_LOCATION: "./assets/images/icon_location.png",
  NO_ORDERS: "./assets/images/no-orders.png",
  BACKWARD_ICON: "./assets/images/backward-outline.png",
} as const;

// Image constants
export const IMAGE = {
  FORMATS: ["PNG", "JPEG", "JPG"],
  ERROR_MESSAGES: {
    INVALID_TYPE: (allowedFormats: string[]) => {
      // Convert MIME types to user-friendly format names
      const formatMap: { [key: string]: string } = {
        'image/png': 'PNG',
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPG',
        'image/svg+xml': 'SVG'
      };
      const userFriendlyFormats = allowedFormats.map(format => formatMap[format] || format);
      return `Invalid image type. Allowed types: ${userFriendlyFormats.join(', ')}`;
    }
  }
} as const;

// Images constants
export const IMAGES = {
  PLUS_CIRCLE: "./assets/images/plus-circle.png",
  PENCIL_SQUARE: "./assets/images/pencil-square.png",
  TRASH: "./assets/images/trash.png",
  NO_ORDERS: "./assets/images/no-orders.png",
  NO_PRODUCTS: "./assets/images/no-products.png",
  NO_BANNER: "./assets/images/image-placeholder.png",
  BACKWARD_OUTLINE: "./assets/images/backward-outline.png",
  ADD_ICON: "./assets/images/plus-circle.png",
  EDIT_ICON: "./assets/images/pencil-square.png",
  DELETE_ICON: "./assets/images/trash.png",
  VIEW_ICON: "./assets/images/view.png",
  CLOCK_ICON: "./assets/images/clock.png",
  ICON_LOCATION: "./assets/images/icon_location.png",
  NO_IMAGE_URL: "./assets/images/image-placeholder.png",
} as const;

// Image dimensions constants
export const IMAGE_DIMENSIONS = {
  DEFAULT_WIDTH: 320,
  DEFAULT_HEIGHT: 320,
  DEFAULT_HEIGHT_PX: 230,
} as const;

// HTTP status constants
export const HTTP_STATUS = {
  NOT_FOUND: 404,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// Custom business status codes
export const CUSTOM_STATUS_CODES = {
  TOKEN_INVALID: 900,
} as const;

// Date format constants
export const DATE_FORMATS = {
  DD_MM_YYYY: "DD/MM/YYYY",
  DD_MM_T_HH_MM: "yyyy-MM-ddTHH:mm",
  DD_MM_T_HH: "yyyy-MM-DDTHH:mm",
  DD_MM_YYYY_HH_MM: "dd/MM/yyyy HH:mm",
  // Timestamp format for filenames, e.g. 14235501012025
  EXPORT_TIMESTAMP: "HHmmssddMMyyyy",
} as const;

// Common constants
export const COMMON = {
  DEFAULT_PAGE: PAGINATION.DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE: PAGINATION.DEFAULT_PAGE_SIZE,
  FIRST_PAGE: PAGINATION.FIRST_PAGE,
  FIRST_ROW_INDEX: PAGINATION.FIRST_ROW_INDEX,
  SUCCESS_TIMER: TIMERS.SUCCESS,
  ERROR_TIMER: TIMERS.ERROR,
  NO_BANNER_URL: URLS.NO_BANNER_URL,
  NO_PRODUCTS_IMAGE: URLS.NO_PRODUCTS_IMAGE,
  EDIT_ICON: URLS.EDIT_ICON,
  DELETE_ICON: URLS.DELETE_ICON,
  ADD_ICON: URLS.ADD_ICON,
  BACKWARD_ICON: URLS.BACKWARD_ICON,
  DD_MM_YYYY: DATE_FORMATS.DD_MM_YYYY,
  IMAGE_FORMATS: IMAGE.FORMATS,
} as const;

// type image
export const TYPE_IMAGE: string[] = ["image/png", "image/jpeg", "image/jpg"];

// Multi upload status constants
export const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading', 
  SUCCESS: 'success',
  ERROR: 'error'
} as const;


export const TAB_CONFIG = {
  ENGLISH: { id: 0, title: "English" },
  KHMER: { id: 1, title: "Khmer" },
};

export const LANG_KEY = {
  ENGLISH: "en",
  KHMER: "kh",
} as const;

// Role display name mapping
export const ROLE_MAP: { [key: string]: string } = {
  'SUPER_ADMIN': 'Super Admin',
  'ADMIN': 'Admin',
  'TENANT': 'Tenant',
  'MALL': 'Mall',
  'ACCOUNT': 'Account',
  'SALE': 'Sale',
  'COUNTER': 'Counter',
  'CUSTOMER_SERVICE': 'Customer Service',
  'TENANT_CONTROL': 'Tenant Control'
} as const;

export const ALL_MALLS = {
  LIMIT: 100,
  OFFSET: 0,
} as const;

// Common symbols
export const SYMBOLS = {
  CLOSE: '×',
  PLUS: '+',
  MINUS: '-',
  CHECK: '✓',
  ARROW_RIGHT: '→',
  ARROW_LEFT: '←',
} as const;

// Language constants
export const LANGUAGES = {
  EN: "en",
  KH: "kh",
} as const;

// Language display constants
export const LANGUAGE_DISPLAY = {
  [LANGUAGES.EN]: "English",
  [LANGUAGES.KH]: "Khmer",
} as const;

export const EMPTY_CONTENT = '<!DOCTYPE html>\n<html>\n<head>\n</head>\n<body>\n\n</body>\n</html>'